/* eslint-disable react-hooks/exhaustive-deps */
/*
 * UsedInModal — shared "What tech unlocks this?" cross-reference panel.
 *
 * Drives a single backend reverse-index (`GET /api/tech-tree/used-in/
 * {material}`) that returns every tech entry whose recipe includes
 * the named material. Used from all three Game Codex tabs:
 *   - Tech Tree    : click a material chip inside the tech detail modal
 *   - Base Structures: click a material chip inside the structure modal
 *   - Items Codex  : "Where this is used" button inside the item modal
 *
 * Clicking a tech row in the result list invokes `onTechClick(name)`,
 * which the parent (`TechTree.jsx`) uses to switch to the Tech Tree
 * tab and open that tech's detail. This is the lift-up that keeps
 * sibling tabs in sync without resorting to a context.
 */
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { api } from "@/lib/api";

const ASSETS = process.env.REACT_APP_BACKEND_URL;

function resolveImg(path) {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  if (path.startsWith("/")) return `${ASSETS}${path}`;
  return `${ASSETS}/api/${path}`;
}

export default function UsedInModal({ material, onClose, onTechClick }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    if (!material) return;
    let cancelled = false;
    setData(null);
    api.get(`/tech-tree/used-in/${encodeURIComponent(material)}`)
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch(() => { /* network/404 — empty state below handles it */ });
    return () => { cancelled = true; };
  }, [material]);

  const matImg = data?.material_image ? resolveImg(data.material_image) : null;

  return (
    <div
      className="fixed inset-0 bg-black/75 z-[60] flex items-center justify-center p-4 backdrop-blur-sm fade-up"
      onClick={onClose}
      data-testid="used-in-modal"
    >
      <div
        className="glass-card relative max-w-xl w-full max-h-[88vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-[var(--muted)] hover:text-[var(--bone)] p-1 rounded-full hover:bg-white/5 z-10"
          aria-label="Close"
          data-testid="used-in-modal-close"
        >
          <X size={18} />
        </button>
        <div className="p-6 sm:p-7">
          <div className="flex items-center gap-3 mb-5">
            {matImg ? (
              <img src={matImg} alt={material} className="w-14 h-14 rounded bg-black/40 object-contain p-1 ring-1 ring-white/10 shrink-0" />
            ) : (
              <div className="w-14 h-14 rounded bg-black/40 ring-1 ring-white/10 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)]">Used in</div>
              <h2 className="text-xl font-semibold truncate" data-testid="used-in-material-name">{material}</h2>
              {data && (
                <div className="text-xs text-[var(--muted)] mt-0.5">
                  Required by <span className="text-[var(--teal)] font-mono">{data.count}</span> tech entr{data.count === 1 ? "y" : "ies"}
                </div>
              )}
            </div>
          </div>
          {!data ? (
            <p className="text-[var(--muted)] text-sm">Loading…</p>
          ) : data.techs.length === 0 ? (
            <p className="text-[var(--muted)] text-sm italic" data-testid="used-in-empty">
              Nothing in the tech tree requires this material.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="used-in-list">
              {data.techs.map((t, i) => {
                const tImg = resolveImg(t.wiki_image);
                return (
                  <li
                    key={`${t.name}-${i}`}
                    onClick={() => onTechClick?.(t.name)}
                    className="flex items-center gap-3 px-3 py-2 rounded bg-black/20 border border-white/5 hover:border-[var(--teal-soft)] hover:bg-[rgba(0,229,255,0.04)] cursor-pointer transition-colors"
                    data-testid={`used-in-row-${i}`}
                  >
                    {tImg ? (
                      <img src={tImg} alt={t.name} className="w-9 h-9 rounded object-contain bg-black/30 ring-1 ring-white/10 shrink-0" loading="lazy" />
                    ) : (
                      <div className="w-9 h-9 rounded bg-black/30 ring-1 ring-white/10 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[var(--bone)] truncate">{t.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">
                        Lv {t.level}{t.source ? ` · ${t.source === "structure" ? "Buildable" : "Craftable"}` : ""}
                      </div>
                    </div>
                    <span className="font-mono text-[var(--teal)] text-sm shrink-0">×{t.qty}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
